import * as React from "react";
import {defineMessages, InjectedIntl, InjectedIntlProps, injectIntl} from "react-intl";
import {connect} from "react-redux";
import {WithApi, WithApiProperties} from "../../common/util/WithApi";
import {Product} from "../../products/model";
import {actions} from "../actions";
import {Service} from "../model";
import {selectors as serviceSelectors} from "../selectors";
import {PublishedProduct} from "./model";
import {Validation} from "./ValidatedTextBox";
import {WMTS_PUBLISH_INFO_TABLE_ROW_HEIGHT, WmtsPublishInfoTable} from "./WmtsPublishInfoTable";

export const FIELD_UPDATE_STATUS_MESSAGES = defineMessages({
  success: {
    id: "studio.services.wmts.publish-info.update-success.message",
    defaultMessage: "{field} changed successfully",
  },
  failure: {
    id: "studio.services.wmts.publish-info.update-failure.message",
    defaultMessage: "{field} update failed with error message: {errorMessage}",
  }
});

export interface PublishedWmtsProductModel {
  serviceId: string;
  productId: string;
  productName: string;
  tileMatrixSets: TileMatrixSet[];
  availableTileMatrixSets: TileMatrixSet[];
  onValueChange: (entity: PublishedWmtsProductModel, newValue: string[]) => Promise<Validation>;
  intl?: InjectedIntl;
}

export interface TileMatrixSet {
  id: string;
  title: string;
}

interface WmtsPublishInfoProps {
  isLoaded: boolean;
  service: Service;
  serviceProducts: Product[];
  servicePublishedProducts: PublishedProduct[];
  loadServicePublishedProducts: () => any;
  selectedModelData: PublishedWmtsProductModel | null;
  selectedProductChanged: (updatedWmtsProduct: PublishedWmtsProductModel | null) => void;
}

interface WmtsPublishInfoState {
  tableModelData: PublishedWmtsProductModel[];
  availableTileMatrixSets: any[];
}

type WmtsPublishInfoComponentProps = WmtsPublishInfoProps & WithApiProperties & InjectedIntlProps;

class WmtsPublishInfoComponent extends React.Component<WmtsPublishInfoComponentProps, WmtsPublishInfoState> {

  constructor(props: WmtsPublishInfoComponentProps) {
    super(props);
    this.state = {
      tableModelData: [],
      availableTileMatrixSets: [],
    };
  }

  componentDidMount() {
    const {isLoaded, loadServicePublishedProducts, selectedProductChanged} = this.props;
    if (isLoaded) {
      loadServicePublishedProducts();
      selectedProductChanged(null);
    }
    this.props.api.getAvailableTileMatrixSets().then((response) => {
      this.setState({availableTileMatrixSets: response?.tileMatrixSets});
    });
  }

  componentDidUpdate(prevProps: WmtsPublishInfoComponentProps) {
    if (prevProps.isLoaded !== this.props.isLoaded && this.props.isLoaded) {
      this.props.loadServicePublishedProducts();
    }

    if ((prevProps.servicePublishedProducts !== this.props.servicePublishedProducts ||
         prevProps.serviceProducts !== this.props.serviceProducts) &&
        this.props.servicePublishedProducts && this.props.serviceProducts) {
      const tableModelData: PublishedWmtsProductModel[] = [];
      if (this.props.serviceProducts?.length > 0) {
        for (const publishedProduct of this.props.servicePublishedProducts) {
          tableModelData.push({
            serviceId: this.props.service.id,
            productId: publishedProduct.id,
            productName: publishedProduct.name,
            tileMatrixSets: publishedProduct.publishInfo?.tileMatrixSets,
            availableTileMatrixSets: this.state.availableTileMatrixSets,
            onValueChange: this.onProductTileMatrixSetsChange,
            intl: this.props.intl
          });
        }
      }

      if (!this.props.isLoaded) {
        if (this.props.selectedModelData) {
          this.props.selectedProductChanged(null);
        }
      } else {
        this.setState({tableModelData});
      }
    }
  }

  componentWillUnmount() {
    this.resetPublishInformationState();
  }

  resetPublishInformationState = () => {
    this.setState({tableModelData: []});
    this.props.selectedProductChanged(null);
  }

  onProductTileMatrixSetsChange = async (entity: PublishedWmtsProductModel,
                                         updatedModelDataSelection: string[]): Promise<Validation> => {
    const {tableModelData} = this.state;
    const productIndex = tableModelData.findIndex(item => entity?.productId === item.productId);
    if (productIndex !== -1) {
      tableModelData[productIndex].tileMatrixSets = updatedModelDataSelection.map(tileMatrixSet => {
        const tmsDetail = this.state.availableTileMatrixSets.find(tms => tms.id === tileMatrixSet);
        return tmsDetail ? {id: tmsDetail.id, title: tmsDetail.title} as TileMatrixSet : null;
      });
      this.setState({tableModelData});
    }

    const {service} = this.props;
    if (!service) {
      return {
        state: "error",
        message: entity.intl.formatMessage(FIELD_UPDATE_STATUS_MESSAGES.failure,
            {field: entity.serviceId, errorMessage: "Service not found"})
      };
    }
    try {
      await this.props.api.updateServiceProductTypeName(service.id, {
        id: entity?.productId,
        publishInfo: {
          serviceType: "wmts",
          tileMatrixSets: updatedModelDataSelection.map(tmsId => ({id: tmsId}))
        }
      } as PublishedProduct);
    } catch (error) {
      return {
        state: "error",
        message: entity.intl.formatMessage(FIELD_UPDATE_STATUS_MESSAGES.failure,
            {field: entity.productName, errorMessage: error.message || "Unknown error"})
      };
    }
    return {
      state: "success",
      message: this.props.intl.formatMessage(FIELD_UPDATE_STATUS_MESSAGES.success, {field: entity.productName})
    };
  }

  render() {
    const {
      service,
      servicePublishedProducts,
      isLoaded,
      intl
    } = this.props;
    const {tableModelData} = this.state;
    if (!service || !servicePublishedProducts) {
      return null;
    }

    return (
        <>
          {isLoaded && (
              <div> {/* This empty div is necessary! it's used by ControlRoomTable to calculate its dimensions.*/}
                  <WmtsPublishInfoTable key={"studio.service.wmtsPublishInfoTable"}
                                        data={tableModelData}
                                        rowHeight={WMTS_PUBLISH_INFO_TABLE_ROW_HEIGHT}
                                        canMultiSelect={false}
                                        isPaged={false}
                                        isAllDataLoaded={true}
                                        intl={intl}/>
              </div>
          )}
        </>
    );
  }
}


const mapStateToProps = (state, ownProps) => {
  const serviceId = ownProps.service.id; //injected by parent
  return {
    servicePublishedProducts: serviceSelectors.getServicePublishedProducts(state, serviceId),
    selectedModelData: serviceSelectors.getSelectedWmtsProduct(state, serviceId),
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const serviceId = ownProps.service.id; //injected by parent
  const productIds: string[] = ownProps.serviceProducts.map((product: Product) => product.id).reverse();

  return {
    loadServicePublishedProducts: () => dispatch(actions.loadServicePublishedProducts(serviceId, productIds)),
    selectedProductChanged: (updatedWmtsProduct: PublishedWmtsProductModel | null) => {
      dispatch(actions.selectedProductChanged(serviceId, updatedWmtsProduct));
    }
  };
};

export const PublishInformationComponent = WithApi(WmtsPublishInfoComponent);

export const WmtsPublishInfo = injectIntl(
    connect(mapStateToProps, mapDispatchToProps)(PublishInformationComponent));
